// File: src/pages/PageMenu/ProgramSettingsPage.jsx

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2' // 👈 ترقية التنبيهات لأسلوب النظام الاحترافي
import axios from '../../axios'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import {
  Select,
  SelectItem
} from '../../components/ui/select'
import { Switch } from '../../components/ui/switch'
import { Label } from '../../components/ui/label'
import { 
  Settings, 
  Print, 
  ToggleOn, 
  Save, 
  Cancel, 
  Palette, 
  DisplaySettings,
  ReceiptLong,
  Store,
  AccountBalance,
  LocalShipping,
  AttachMoney,
  CheckCircle,
} from '@mui/icons-material'

export default function ProgramSettingsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const companyId = parseInt(localStorage.getItem('companyId'), 10)
  const isRTL = i18n.language === 'ar'

  const [setting, setSetting] = useState({
    id: null,
    companyId,
    invoiceTypeId: null,
    decimalPlaces: 2,
    isPriceIncludesVAT: false,
    autoPrintAfterSave: false,
    autoApproveInvoices: false,
    printCopies: 1,
    defaultCustomerId: null,
    defaultPaymentMethodId: null,
    defaultSafeId: null,
    defaultBankId: null,
    defaultStockId: null,
    defaultTaxTypeId: null,
    allowNegativeSales: false,
    pricingPolicy: '',
    defaultPrinterName: '',
    allowedSecondaryPrinterCount: 2,
    secondaryPrinterNames: []
  })

  const [lists, setLists] = useState({
    invoiceTypes: [],
    customers: [],
    payments: [],
    safes: [],
    banks: [],
    stocks: [],
    taxes: [],
    printers: []
  })

  const [loading, setLoading] = useState(false)

  // 1. Load base settings & lookup lists
  useEffect(() => {
    if (!companyId) return

    axios
      .get('/ProgramSettings/by-company', { params: { companyId } })
      .then(res => setSetting(prev => ({ ...prev, ...res.data })))
      .catch(err => console.warn('❌', t('programSettings.messages.loadSettingsFailed'), err))

    Promise.all([
      axios.get('/InvoiceTypes', { params: { companyId } }),
      axios.get(`/Customer/by-company/${companyId}`),
      axios.get(`/PaymentMethod/by-company/${companyId}`),
      axios.get(`/Safe/by-company/${companyId}`),
      axios.get(`/Bank/by-company/${companyId}`),
      axios.get(`/Stock/by-company/${companyId}`),
      axios.get(`/VatTypes`, { params: { companyId } }),
      axios.get('/Printers').then(res => ({ data: res.data.map(p => ({ id: p, name: p })) })).catch(() => ({ data: [] }))
    ])
      .then(([inv, cust, pay, safe, bank, stock, tax, printers]) => {
        setLists({
          invoiceTypes: inv.data,
          customers: cust.data,
          payments: pay.data,
          safes: safe.data,
          banks: bank.data,
          stocks: stock.data,
          taxes: tax.data,
          printers: printers.data
        })
      })
      .catch(err => console.error('❌', t('programSettings.messages.loadListsFailed'), err))
  }, [companyId, t])

  // 2. Reload when invoiceTypeId changes
  useEffect(() => {
    if (!companyId || setting.invoiceTypeId == null) return

    axios
      .get('/ProgramSettings/by-company-and-type', {
        params: { companyId, invoiceTypeId: setting.invoiceTypeId }
      })
      .then(res => setSetting(prev => ({ ...prev, ...res.data })))
      .catch(() => console.warn('❌', t('programSettings.messages.noSettingsForType')))
  }, [companyId, setting.invoiceTypeId, t])

  // 3. Save or update
  const handleSave = async () => {
    if (setting.invoiceTypeId == null) {
      Swal.fire({
        icon: 'error',
        title: isRTL ? 'تنبيه' : 'Alert',
        text: t('programSettings.messages.selectInvoiceTypeFirst') || 'الرجاء اختيار نوع الفاتورة أولاً',
        confirmButtonColor: '#4F46E5',
      })
      return
    }
    setLoading(true)

    const {
      id,
      companyId: cId,
      invoiceTypeId,
      secondaryPrinterNames,
      ...rest
    } = setting

    const payload = {
      companyId: cId,
      invoiceTypeId,
      secondaryPrinterNames,
      ...rest
    }

    try {
      if (id) {
        await axios.put(`/ProgramSettings/${id}`, payload)
        Swal.fire({
          icon: 'success',
          title: isRTL ? 'تم التحديث بنجاح' : 'Updated Successfully',
          timer: 1500,
          showConfirmButton: false
        })
      } else {
        const res = await axios.post('/ProgramSettings', payload)
        setSetting(prev => ({ ...prev, id: res.data.id }))
        Swal.fire({
          icon: 'success',
          title: isRTL ? 'تم الحفظ بنجاح' : 'Created Successfully',
          timer: 1500,
          showConfirmButton: false
        })
      }
    } catch (err) {
      console.error('❌ SERVER RESPONDED:', err.response?.status, err.response?.data)
      Swal.fire({
        icon: 'error',
        title: isRTL ? 'فشل الحفظ' : 'Save Failed',
        text: `HTTP ${err.response?.status}`,
        confirmButtonColor: '#EF4444',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setSetting(prev => ({ ...prev, [field]: value }))
  }

  // renderInput helper المطور بحواف زجاجية متناسقة
  const renderInput = (labelText, field, icon = null) => (
    <div key={field} className="flex flex-col gap-2">
      <Label className="font-bold text-slate-700 text-sm flex items-center gap-2">
        {icon}
        {labelText}
      </Label>
      <Input
        className="w-full h-10 text-sm font-semibold px-3 border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all bg-white"
        type="number"
        value={setting[field] ?? ''}
        onChange={e => handleChange(field, parseInt(e.target.value || '0', 10))}
      />
    </div>
  )

  // renderSelect helper المطور
  const renderSelect = (
    labelText,
    field,
    options = [],
    placeholder = t('programSettings.selectOption'),
    icon = null
  ) => {
    const val = setting[field] != null ? String(setting[field]) : ''

    return (
      <div key={field} className="flex flex-col gap-2">
        <Label className="font-bold text-slate-700 text-sm flex items-center gap-2">
          {icon}
          {labelText}
        </Label>
        <Select
          value={val}
          onValueChange={v => handleChange(field, parseInt(v, 10))}
        >
          <div className="w-full h-10 text-sm font-semibold border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white">
            {options.map(o => (
              <SelectItem key={o.id} value={String(o.id)}>
                {o.name || o.nameAr || o.nameEn}
              </SelectItem>
            ))}
          </div>
        </Select>
      </div>
    )
  }

  const renderSelectString = (
    labelText,
    field,
    options = [],
    placeholder = t('programSettings.selectOption'),
    icon = null
  ) => {
    const val = setting[field] || ''

    return (
      <div key={field} className="flex flex-col gap-2">
        <Label className="font-bold text-slate-700 text-sm flex items-center gap-2">
          {icon}
          {labelText}
        </Label>
        <Select
          value={val}
          onValueChange={v => handleChange(field, v)}
        >
          <div className="w-full h-10 text-sm font-semibold border border-slate-200 rounded-xl bg-white">
            {options.map(o => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </div>
        </Select>
      </div>
    )
  }

  const renderSelectinv = (
    labelText,
    field,
    options = [],
    placeholder = t('programSettings.selectOption'),
    valueKey = 'invoiceTypeId',
    labelKeys = ['name', 'nameAr', 'nameEn'],
    icon = null
  ) => {
    const val = setting[field] != null ? String(setting[field]) : ''

    return (
      <div key={field} className="flex flex-col gap-2">
        <Label className="font-bold text-slate-700 text-sm flex items-center gap-2">
          {icon}
          {labelText}
        </Label>

        <Select
          value={val}
          onValueChange={v => handleChange(field, Number(v))}
        >
          <div className="w-full h-10 text-sm font-semibold border border-slate-200 rounded-xl bg-white">
            {options.map(o => {
              const label = labelKeys.map(k => o[k]).find(Boolean) || ''
              return (
                <SelectItem key={o[valueKey]} value={String(o[valueKey])}>
                  {label}
                </SelectItem>
              )
            })}
          </div>
        </Select>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8 px-4 md:px-6" style={{ fontFamily: 'Alexandria' }}>
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* هيدر تدرج النصوص الفاخر الموحد للنظام */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <Settings fontSize="large" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">{t('programSettings.title') || 'إعدادات البرنامج'}</h1>
              <p className="text-slate-500 text-sm mt-0.5 font-medium">قم بتخصيص خيارات الضرائب، القيود، الطابعات، والخصائص الافتراضية للفواتير</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline"
              className="gap-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50"
            >
              <Cancel fontSize="small" />
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={loading}
              className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-md shadow-indigo-600/10 transition-all"
            >
              {loading ? <span className="animate-spin">⌛</span> : <Save fontSize="small" />}
              {loading ? t('programSettings.saving') : t('programSettings.saveSettings')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* العمود الأيسر - الكروت الرئيسية */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* 1. Basic Settings */}
            <Card className="shadow-sm border border-slate-100 bg-white rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-50/50 to-white border-b border-slate-50 pb-4">
                <CardTitle className="text-md font-bold text-purple-800 flex items-center gap-2">
                  <DisplaySettings fontSize="medium" />
                  {t('programSettings.basicSettings') || 'الإعدادات الأساسية'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    {renderSelectinv(t('programSettings.invoiceType'), 'invoiceTypeId', lists.invoiceTypes, undefined, undefined, undefined, <ReceiptLong fontSize="small" className="text-gray-500" />)}
                  </div>
                  {renderInput(t('programSettings.decimalPlaces'), 'decimalPlaces', <span className="text-gray-500 text-xs font-mono">.00</span>)}
                  {renderInput(t('programSettings.printCopies'), 'printCopies', <Print fontSize="small" className="text-gray-500" />)}
                </div>
              </CardContent>
            </Card>

            {/* 2. Default Settings */}
            <Card className="shadow-sm border border-slate-100 bg-white rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-50/50 to-white border-b border-slate-50 pb-4">
                <CardTitle className="text-md font-bold text-emerald-800 flex items-center gap-2">
                  <Store fontSize="medium" />
                  {t('programSettings.defaultSettings') || 'الإعدادات الافتراضية'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderSelect(t('programSettings.defaultCustomer'), 'defaultCustomerId', lists.customers, undefined, <CheckCircle fontSize="small" className="text-gray-500" />)}
                  {renderSelect(t('programSettings.defaultPaymentMethod'), 'defaultPaymentMethodId', lists.payments, undefined, <AttachMoney fontSize="small" className="text-gray-500" />)}
                  {renderSelect(t('programSettings.defaultSafe'), 'defaultSafeId', lists.safes, undefined, <AccountBalance fontSize="small" className="text-gray-500" />)}
                  {renderSelect(t('programSettings.defaultBank'), 'defaultBankId', lists.banks, undefined, <AccountBalance fontSize="small" className="text-gray-500" />)}
                  {renderSelect(t('programSettings.defaultStock'), 'defaultStockId', lists.stocks, undefined, <LocalShipping fontSize="small" className="text-gray-500" />)}
                  {renderSelect(t('programSettings.defaultTaxType'), 'defaultTaxTypeId', lists.taxes, undefined, <span className="text-gray-500 text-xs">%</span>)}
                </div>
              </CardContent>
            </Card>

            {/* 3. Printer Settings */}
            <Card className="shadow-sm border border-slate-100 bg-white rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50/50 to-white border-b border-slate-50 pb-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-md font-bold text-blue-800 flex items-center gap-2">
                    <Print fontSize="medium" />
                    {t('programSettings.printerSettings') || 'إعدادات الطابعة'}
                  </CardTitle>
                  <Button 
                    onClick={() => navigate('/print-layout')}
                    variant="ghost"
                    size="sm"
                    className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 font-bold rounded-xl gap-1"
                  >
                    <Palette fontSize="small" />
                    {t('programSettings.openPrintDesigner') || 'مصمم الطباعة'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 gap-6">
                  {renderSelectString(t('programSettings.defaultPrinter'), 'defaultPrinterName', lists.printers, t('programSettings.selectPrinter'), <Print fontSize="small" className="text-gray-500" />)}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <Label className="font-bold text-slate-700 text-sm flex items-center gap-2">
                        <Print fontSize="small" className="text-gray-500" />
                        {t('programSettings.secondaryPrinters')}
                      </Label>
                      <select
                        multiple
                        className="w-full border border-slate-200 rounded-xl p-3 h-32 text-sm font-semibold focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 bg-white"
                        value={setting.secondaryPrinterNames}
                        onChange={e => {
                          const selected = Array.from(e.target.selectedOptions, opt => opt.value)
                          if (selected.length <= setting.allowedSecondaryPrinterCount) {
                            handleChange('secondaryPrinterNames', selected)
                          } else {
                            Swal.fire({
                              icon: 'warning',
                              text: t('programSettings.messages.maxSecondaryPrinters', { count: setting.allowedSecondaryPrinterCount })
                            })
                          }
                        }}
                      >
                        {lists.printers.map(p => (
                          <option key={p.id} value={p.id} className="py-1.5 px-2 cursor-pointer hover:bg-indigo-50 rounded-lg">{p.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-400 font-medium">استخدم زر Ctrl في الكيبورد لتحديد أكثر من طابعة معاً</p>
                    </div>

                    <div className="flex flex-col justify-start">
                       {renderInput(t('programSettings.allowedSecondaryPrinterCount'), 'allowedSecondaryPrinterCount', <span className="text-gray-500 text-xs">#</span>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* العمود الأيمن - خيارات التشغيل التفاعلية */}
          <div className="lg:col-span-4 space-y-6">
            
            <Card className="shadow-sm border border-slate-100 bg-white rounded-2xl overflow-hidden sticky top-6">
              <CardHeader className="bg-gradient-to-r from-orange-50/50 to-white border-b border-slate-50 pb-4">
                <CardTitle className="text-md font-bold text-orange-800 flex items-center gap-2">
                  <ToggleOn fontSize="medium" />
                  {t('programSettings.toggleSettings') || 'خيارات التشغيل'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-50">
                  {[
                    ['isPriceIncludesVAT', t('programSettings.priceIncludesVAT')],
                    ['autoApproveInvoices', t('programSettings.autoApproveInvoices')],
                    ['autoPrintAfterSave', t('programSettings.autoPrintAfterSave')],
                    ['allowNegativeSales', t('programSettings.allowNegativeSales')]
                  ].map(([field, label]) => (
                    <div
                      key={field}
                      className="flex items-center justify-between p-4.5 hover:bg-slate-50/50 transition-colors"
                    >
                      <Label htmlFor={field} className="font-bold text-sm text-slate-700 cursor-pointer flex-1">{label}</Label>
                      <Switch
                        id={field}
                        checked={setting[field]}
                        onCheckedChange={val => handleChange(field, val)}
                        className="data-[state=checked]:bg-orange-500"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  )
}